import React from 'react';

import { StyleSheet } from 'react-native';
import type { TextStyle } from 'react-native';

import ActionButton, { ActionButtonVariants, IActionButtonProps } from 'components/action-button';
import { Box, BoxProps, Typography, TypographyProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { primitive, spacing } from 'styles/constants';
import { WEB_APP_MODAL_CONTENT_MAX_WIDTH } from 'utils/constants';
import { isWeb } from 'utils/environment';

const commonMarginTop = spacing.$4;

type ActionButtonWrapperProps = Omit<IActionButtonProps, 'children'> & {
  children?: React.ReactNode;
};

export const DisclaimerText = ({ style, ...props }: TypographyProps) => (
  <Typography
    color={primitive.$bbqBrown}
    focusable
    style={[styles.disclaimerText, style]}
    textAlign="center"
    {...props}
  />
);

export const StyledActionButton = (props: ActionButtonWrapperProps) => (
  <Box style={styles.actionButton}>
    <ActionButton {...props} />
  </Box>
);

export const ActionButtonOutline = ({
  color = primitive.$bbqBrown,
  variant = ActionButtonVariants.OUTLINE,
  ...props
}: ActionButtonWrapperProps) => (
  <StyledActionButton
    borderColor={primitive.$bbqBrown}
    color={color}
    variant={variant}
    {...props}
  />
);

export const Container = ({ style, ...props }: BoxProps) => (
  <Box style={[styles.container, style]} {...props} />
);

export const StoreAddressText = ({ style, ...props }: TypographyProps) => (
  <Typography
    color={primitive.$bbqBrown}
    fontFamily="body"
    fontSize="md"
    numberOfLines={1}
    style={[styles.storeAddressText, isWeb && styles.storeAddressTextWeb, style]}
    {...props}
  />
);

export const HeaderText = ({ style, ...props }: TypographyProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      accessible
      accessibilityRole="header"
      style={[
        styles.headerText,
        isWeb && styles.headerTextWeb,
        isDesktop && styles.headerTextDesktop,
        style,
      ]}
      variant="headerOne"
      {...props}
    />
  );
};

export const DistanceText = ({ style, ...props }: TypographyProps) => (
  <Typography fontFamily="heading" fontSize="sm" lineHeight="xs" style={style} {...props} />
);

export const StoreDetailsContainer = ({ style, ...props }: BoxProps) => (
  <Box style={[styles.storeDetailsContainer, style]} {...props} />
);

const styles = StyleSheet.create({
  actionButton: {
    marginTop: commonMarginTop,
    width: '100%',
  },
  container: {
    maxWidth: WEB_APP_MODAL_CONTENT_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$9,
    width: '100%',
  },
  disclaimerText: {
    marginTop: commonMarginTop,
  },
  headerText: {
    margin: 0,
    textAlign: 'center',
  },
  headerTextDesktop: {
    fontSize: 24,
  },
  headerTextWeb: {
    fontSize: 20,
  },
  storeAddressText: {
    flex: 1,
    overflow: 'hidden',
  },
  storeAddressTextWeb: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as TextStyle,
  storeDetailsContainer: {
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: primitive.$blackOpacity10,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: commonMarginTop,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
  },
});
